import type { FastifyRequest, FastifyReply } from 'fastify'
import type { AuthUser } from '../../core/auth/auth0.js'
import type { UserRole } from '@my-roadtrip/types'

function getUser(request: FastifyRequest): AuthUser | undefined {
  return (request as FastifyRequest & { user?: AuthUser }).user
}

export function hasRole(user: AuthUser | undefined, role: UserRole): boolean {
  if (!user) return false
  // Accept explicit role property, boolean admin flag, and roles array
  if (user.role === role) return true
  if (role === 'admin' && user.admin === true) return true
  return Array.isArray(user.roles) ? user.roles.includes(role) : false
}

export function hasAnyRole(user: AuthUser | undefined, roles: UserRole[]): boolean {
  if (!user) return false
  for (const role of roles) {
    if (hasRole(user, role)) {
      return true
    }
  }
  return false
}

export function requireRole(...roles: UserRole[]) {
  return async (request: FastifyRequest, reply: FastifyReply): Promise<void> => {
    try {
      const user = getUser(request)

      if (!user) {
        await reply.status(401).send({ error: 'Unauthorized' })
        return
      }

      if (!hasAnyRole(user, roles)) {
        await reply.status(403).send({ error: 'Insufficient permissions' })
        return
      }
    } catch (error) {
      request.log.error(error)
      await reply.status(500).send({ error: 'Authorization check failed' })
    }
  }
}

export async function adminMiddleware(request: FastifyRequest, reply: FastifyReply): Promise<void> {
  const guard = requireRole('admin', 'superadmin')
  await guard(request, reply)
}
